"use client";

import { useState, useEffect, useTransition } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import ScriptPreview, {
  ScriptPreviewSkeleton,
} from "@/components/script/script-preview";
import FilterDropdown from "@/components/script/filter-dropdown";
import { useInfiniteQuery } from "@tanstack/react-query";
import { env } from "@/env";
import LoadingSpinner from "@/components/ui/loading-spinner";
import { useFilter } from "@/hooks/use-filter";
import { useDebouncedCallback } from "use-debounce";
import type { PaginatedScripts } from "@/types/script";
import Link from "next/link";

const ITEMS_PER_PAGE = 16;

export default function ScriptInfiniteList({
  initialParams,
  initialData,
}: {
  initialParams?: {
    query?: string;
  };
  initialData?: PaginatedScripts;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const query = searchParams.get("query") ?? "";
  const [searchInput, setSearchInput] = useState(initialParams?.query ?? query);

  // when the URL changes externally, keep the input value in sync
  useEffect(() => {
    setSearchInput(query);
  }, [query]);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [windowWidth, setWindowWidth] = useState<number | null>(null);
  const filter = useFilter((state) => state.filters);
  const [isPending, startTransition] = useTransition();

  function buildUrl(baseUrl: string, params: Record<string, string | number>) {
    const validParams = Object.entries(params)
      .filter(([_, value]) => value !== "")
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join("&");

    return `${baseUrl}?${validParams}`;
  }

  const fetchScripts = async ({ pageParam = 1 }) => {
    const currentPage = pageParam;
    const fetchParams = {
      query,
      page: currentPage,
      limit: ITEMS_PER_PAGE,
      mobile: filter.mobile ? "true" : "",
      payment: filter.payment ?? "",
      sortBy: filter.sortBy ?? "",
      orderBy:
        filter.orderBy === "asc" || filter.orderBy === "desc"
          ? filter.orderBy
          : "desc",
      key_system: filter.key_system ? "true" : "",
    } as Record<string, string | number>;

    const url = buildUrl(`${env.NEXT_PUBLIC_API_URL}/post/search`, fetchParams);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error("Failed to fetch scripts");
    }
    return response.json() as Promise<PaginatedScripts>;
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: [
      "scripts-infinite",
      query,
      filter.mobile,
      filter.payment,
      filter.sortBy,
      filter.orderBy,
      filter.key_system,
    ],
    queryFn: fetchScripts,
    getNextPageParam: (lastPage) => {
      if (lastPage.data.posts.hasNextPage) {
        return lastPage.data.posts.page + 1;
      }
      return undefined;
    },
    initialData: initialData
      ? { pages: [initialData], pageParams: [1] }
      : undefined,
    initialPageParam: 1,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const scripts = data?.pages.flatMap((page) => page.data.posts.result) ?? [];

  const debouncedSearch = useDebouncedCallback((value: string) => {
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("query", value);
    router.push(`?${newParams.toString()}`);
  }, 300);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setErrorMessage(null);
    debouncedSearch(searchInput);
  }

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }

    if (typeof window !== "undefined") {
      setWindowWidth(window.innerWidth);
      window.addEventListener("resize", handleResize);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, []);

  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span>{(error as Error).message}</span>
      </div>
    );
  }

  return (
    <>
      <form
        onSubmit={handleSearch}
        className="mt-8 flex w-full flex-row justify-between gap-1.5"
      >
        <div className="relative flex w-full items-center">
          <MagnifyingGlassIcon className="absolute left-5 mr-2 h-4 w-4" />
          <Input
            placeholder="Start typing to search..."
            className="h-14 rounded-full pl-11 pr-9 bg-white/5"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Button
            type="submit"
            className="w-18 absolute right-3 h-9 rounded-3xl bg-primary px-4 hover:bg-primary/90"
          >
            Search
          </Button>
        </div>
      </form>
      {errorMessage && <p className="mt-2 text-destructive">{errorMessage}</p>}

      <div className="mt-8 flex w-full flex-col rounded-md border border-white/5 bg-white/10 backdrop-blur">
        <div className="flex items-center justify-between gap-3 border-b border-white/5 px-6 py-4 lg:px-8">
          <div className="flex items-center gap-4">
            <span className="flex flex-row items-center gap-1">
              <h2 className="font-heading text-balance text-lg font-medium tracking-tight lg:text-xl">
                {query ? `Results for "${query}"` : "Latest Scripts"}
              </h2>
              <Link href="/scripts/new">
                <Button variant="link">Add your script</Button>
              </Link>
            </span>
          </div>
          <FilterDropdown />
        </div>

        {isLoading ? (
          <div className="min-h-[600px] w-full p-6 lg:p-8">
            <ul className="grid grid-cols-1 gap-x-1.5 gap-y-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                <ScriptPreviewSkeleton key={`initial-loading-${index}`} />
              ))}
            </ul>
          </div>
        ) : scripts.length > 0 ? (
          <div className="min-h-[600px] w-full p-6 lg:p-8">
            <ul className="grid grid-cols-1 gap-x-1.5 gap-y-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {scripts.map((script, index) => (
                <ScriptPreview key={script.slug} {...script} index={index} />
              ))}
              {isFetchingNextPage &&
                Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                  <ScriptPreviewSkeleton key={`page-loading-${index}`} />
                ))}
            </ul>
          </div>
        ) : (
          <div className="flex min-h-[600px] w-full items-center justify-center">
            <span>No scripts found.</span>
          </div>
        )}

        {hasNextPage && (
          <div className="mb-8 flex justify-center">
            <Button
              onClick={() => {
                startTransition(() => {
                  fetchNextPage();
                });
              }}
              disabled={isFetchingNextPage}
              variant="secondary"
              className="border border-white/5 bg-white/10"
            >
              {isFetchingNextPage ? <LoadingSpinner /> : "Load More"}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
